import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Toolbar,
  Typography,
  Alert,
  CircularProgress,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Assessment as AssessmentIcon,
  Help as HelpIcon,
  BugReport as BugReportIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import Logo from '../../assets/SRMD-Logo.png';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import axios from 'axios';
import { getColorScheme } from '../../utils/formatUtils';
import Tooltip from '@mui/material/Tooltip';
import userServiceInstance from '../../services/UserService';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import ExpirationBanner from './ExpirationBanner';
import { useTheme } from '@mui/material/styles';
import { 
  getVibrantChipTextColor, 
  getVibrantChipBorderColor,
  getVibrantIconColor
} from '../../themes/VibrantTheme';

export const PageHeader = ({ 
  darkMode = false, 
  toggleDarkMode, 
  handleLogout, 
  showLogout = false, 
  showDarkMode = true, 
  inVideoEditor = false,
  themeVariant = 'minimalist'
}) => {
  const [userInfo, setUserInfo] = useState(null);
  const [languageAccess, setLanguageAccess] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const userMenuOpen = Boolean(userMenuAnchorEl);
  
  // Password change dialog states
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    // Load user data from localStorage
    try {
      const authUser = JSON.parse(localStorage.getItem('authUser'));
      if (authUser) {
        setUserInfo(authUser);
        setLanguageAccess(authUser.languageAccess || {});
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  }, []);
  
  // Handler for any click on language chips or the +X chips
  const handleLanguageChipClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };

  // User profile menu handlers
  const handleUserMenuClick = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  // Change Password handlers
  const handleChangePassword = () => {
    // Open password dialog and close user menu
    handleUserMenuClose();
    setChangePasswordOpen(true);
    // Reset form state
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordErrors({});
    setPasswordSuccess(false);
  };
  
  const handleClosePasswordDialog = () => {
    setChangePasswordOpen(false);
  };
  
  const validatePasswordForm = () => {
    const errors = {};
    
    if (!currentPassword) {
      errors.currentPassword = "Current password is required";
    }
    
    if (!newPassword) {
      errors.newPassword = "New password is required";
    } else if (newPassword.length < 6) {
      errors.newPassword = "Password must be at least 6 characters";
    }
    
    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your new password";
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = "Passwords don't match";
    }
    
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handlePasswordSubmit = async () => {
    if (!validatePasswordForm()) {
      return;
    }
    
    setPasswordLoading(true);
    setPasswordErrors({});
    
    try {
      
      const response = await userServiceInstance.changePassword(
        currentPassword, 
        newPassword
      );
      
      if (response.data.success) {
        setPasswordSuccess(true);
        
        // Close dialog after success message is shown for a moment
        setTimeout(() => {
          setChangePasswordOpen(false);
          setPasswordSuccess(false);
        }, 2000);
      } else {
        setPasswordErrors({
          form: response.data.message || "Failed to change password"
        });
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordErrors({
        form: error.response?.data?.message || "An error occurred while changing password"
      });
    } finally {
      setPasswordLoading(false);
    }
  };
  
  // Get languages with different access levels
  const readLanguages = Object.entries(languageAccess)
    .filter(([_, access]) => access === 'READ')
    .map(([lang]) => lang);
  
  const writeLanguages = Object.entries(languageAccess)
    .filter(([_, access]) => access === 'WRITE')
    .map(([lang]) => lang);
    
  const adminLanguages = Object.entries(languageAccess)
    .filter(([_, access]) => access === 'ADMIN')
    .map(([lang]) => lang);
    
  // Function to render a group of language chips with the same access level
  const renderLanguageChips = (languages, accessType) => {
    if (languages.length === 0) return null;
    
    const colorScheme = getColorScheme(accessType);
    const isVibrant = themeVariant === 'vibrant';
    
    return (
      <Box sx={{ display: 'flex', ml: 0.5, flexWrap: 'nowrap' }}>
        {languages.slice(0, 1).map(lang => (
          <Tooltip key={lang} title={`${accessType.toLowerCase()} access`}>
            <Chip
              label={lang}
              size="small"
              onClick={handleLanguageChipClick}
              sx={{ 
                mr: 0.5,
                ...(isVibrant ? {
                  backgroundColor: darkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.85)',
                  color: getVibrantChipTextColor(accessType, darkMode),
                  border: '1px solid',
                  borderColor: getVibrantChipBorderColor(accessType),
                  fontWeight: 'bold',
                  '& .MuiChip-label': { 
                    px: 1, 
                    fontSize: '0.7rem',
                    fontWeight: 'bold' 
                  },
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: darkMode ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.95)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  }
                } : {
                  backgroundColor: colorScheme.bg,
                  color: colorScheme.color,
                  border: '1px solid',
                  borderColor: colorScheme.border,
                  '& .MuiChip-label': { px: 1, fontSize: '0.7rem' },
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: `${colorScheme.border}20`
                  }
                })
              }}
            />
          </Tooltip>
        ))}
        
        {/* Display +X chip if there are more languages */}
        {languages.length > 1 && (
          <Tooltip title={`${languages.length - 1} more ${accessType.toLowerCase()}-access languages`}>
            <Chip
              label={`+${languages.length - 1}`}
              size="small"
              onClick={handleLanguageChipClick}
              sx={{ 
                mr: 0.5,
                ...(isVibrant ? {
                  backgroundColor: darkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)',
                  color: getVibrantChipTextColor(accessType, darkMode),
                  border: '1px solid',
                  borderColor: getVibrantChipBorderColor(accessType),
                  '& .MuiChip-label': { 
                    px: 1, 
                    fontSize: '0.7rem',
                    fontWeight: 'bold' 
                  },
                  boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: darkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.8)',
                    boxShadow: '0 2px 3px rgba(0,0,0,0.2)',
                  }
                } : {
                  backgroundColor: `${colorScheme.border}10`,
                  color: colorScheme.color,
                  cursor: 'pointer',
                  '& .MuiChip-label': { px: 1, fontSize: '0.7rem' },
                  '&:hover': {
                    backgroundColor: `${colorScheme.border}20`
                  }
                })
              }}
            />
          </Tooltip>
        )}
      </Box>
    );
  };
    
  const handleNavigateToStatus = () => {
    handleUserMenuClose(); // Close the menu
    navigate('/status'); // Navigate to status page
  };

  const handleNavigateToProcessingStatus = () => {
    handleUserMenuClose(); // Close the menu
    navigate('/status/processing-status'); // Navigate to status page
  };

  const handleNavigateToHelp = () => {
    handleUserMenuClose();
    navigate('/help');
  };

  const handleNavigateToBugReport = () => {
    handleUserMenuClose();
    navigate('/bug-report');
  };

  const handleNavigateToAdmin = () => {
    handleUserMenuClose();
    navigate('/admin');
  };

  const handleNavigateToActiveSessions = () => {
    handleUserMenuClose(); // Close the menu
    navigate('/sessions/active'); // Navigate to active sessions page
  };

  const handleNavigateToTrashBin = () => {
    navigate('/trash');
    handleClose();
  };

  const handleNavigateToEditingStats = () => {
    handleUserMenuClose();
    navigate('/editing-stats');
  };

  // Update your existing PageHeader to use theme properties:

  // Near the beginning of your component, extract these props:
  const getAppBarStyle = () => {
    if (themeVariant === 'vibrant') {
      return {
        backgroundImage: darkMode 
          ? 'linear-gradient(135deg, #3f4e63 0%, #575872 50%, #7a5667 100%)' // Dark mode: muted blue-purple-mauve
          : 'linear-gradient(135deg, #6a85b6 0%, #8b6cb5 50%, #baa5ff 100%)', // Light mode: lavender-purple gradient
        color: '#ffffff',
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.12)',
        width: '100%'
      };
    } else if (themeVariant === 'minimalist') {
      return {
        backgroundColor: darkMode ? '#272a3d' : '#ffffff',
        color: darkMode ? '#e6e7f0' : '#32354c',
        boxShadow: darkMode
          ? '0 2px 10px rgba(0, 0, 0, 0.2)'
          : '0 2px 10px rgba(0, 0, 0, 0.05)',
        borderRadius: '8px',
        margin: '0 16px 16px 16px',
        width: 'auto'
      };
    } else if (themeVariant === 'corporate') {
      return {
        backgroundColor: darkMode ? '#242c35' : '#ffffff',
        color: darkMode ? '#e0e4e9' : '#3e4c59',
        borderBottom: '1px solid',
        borderColor: darkMode ? '#313c47' : '#e8ecf1'
      };
    } else if (themeVariant === 'materialYou') {
      return {
        backgroundColor: darkMode ? '#242929' : '#ffffff',
        color: darkMode ? '#e1e3e3' : '#191c1c',
        borderBottom: '1px solid',
        borderColor: darkMode ? '#303535' : '#e0e5e5'
      };
    } else if (themeVariant === 'sunset') {
      return {
        backgroundImage: darkMode 
          ? 'linear-gradient(90deg, #4e2611 0%, #3e1c0a 100%)'
          : 'linear-gradient(90deg, #e65100 0%, #b71c1c 100%)',
        color: '#ffffff'
      };
    } else if (themeVariant === 'ocean') {
      return {
        backgroundColor: darkMode ? '#0c2e3e' : '#ffffff',
        color: darkMode ? '#e1f5fe' : '#0d3446',
        boxShadow: darkMode
          ? '0 2px 12px rgba(0, 0, 0, 0.3)'
          : '0 2px 12px rgba(2, 119, 189, 0.1)',
        borderBottom: '1px solid',
        borderColor: darkMode ? '#051923' : '#e1f5fe'
      };
    }
    // Default fallback
    return {
      backgroundColor: darkMode ? '#333' : '#fff',
      color: darkMode ? '#fff' : '#333',
      borderRadius: '8px',
      margin: '0 16px 16px 16px',
      width: 'auto'
    };
  };

  return (
    <>
      <AppBar 
        position="relative" 
        color="default" 
        elevation={0}
        sx={{ 
          ...getAppBarStyle(),
          borderBottom: '1px solid',
          borderColor: darkMode ? 'grey.800' : 'grey.300',
          mb: 2,
          overflow: 'hidden',
          margin: 0,
          borderRadius: 0
        }}
      >
        
        <Toolbar>
          <Tooltip title="Go to Home">
            <Box 
              component={Link} 
              to="/" 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                flexGrow: 1,
                textDecoration: 'none',
                color: 'inherit',
                borderRadius: 1,
                transition: 'background-color 0.2s',
                '&:hover': {
                  backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
                },
                px: 1
              }} 
              className="header-logo"
            >
              <Avatar 
                src={Logo} 
                alt="SRMD Logo" 
                sx={{ width: 40, height: 40, mr: 1 }}
              />
              <Box sx={{ flexGrow: 1 }}>
                <Typography 
                    variant="h5" 
                    component="h1" 
                    sx={{ 
                        fontWeight: 600,
                        color: themeVariant === 'vibrant' ? '#ffffff' : 'primary.main',
                    }}
                >
                    Vaani
                </Typography>
                <Typography
                    variant="subtitle1"
                    sx={{ 
                        color: inVideoEditor 
                            ? 'rgba(255, 255, 255, 0.8)' 
                            : themeVariant === 'vibrant' 
                                ? 'rgba(255, 255, 255, 0.9)' // Better contrast in vibrant theme
                                : 'text.secondary', 
                        fontSize: '0.85rem',
                        fontStyle: 'italic',
                        ...(themeVariant === 'vibrant' && {
                            textShadow: '0 1px 2px rgba(0,0,0,0.2)', // Add text shadow for better readability
                            fontWeight: 500, // Slightly bolder text
                            letterSpacing: '0.01em' // Slightly increased letter spacing
                        })
                    }}
                >
                    Realise One's True Self and Serve Others Selflessly
                </Typography>
              </Box>
            </Box>
          </Tooltip>
          
          {/* User Information & Language Access */}
          {userInfo && (
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }} className="header-language-chips">
              
              
              {/* All language access chips are now clickable */}
              {renderLanguageChips(readLanguages, 'READ')}
              {renderLanguageChips(writeLanguages, 'WRITE')}
              {renderLanguageChips(adminLanguages, 'ADMIN')}
              
              {/* Dropdown menu showing all languages and access levels */}
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                className="header-user-menu"
              >
                <MenuItem sx={{ pointerEvents: 'none', opacity: 0.7 }}>
                  <Typography variant="body2"><b>Language Access Levels</b></Typography>
                </MenuItem>
                <Divider />
                {Object.keys(languageAccess).length > 0 ? (
                  Object.entries(languageAccess).map(([lang, access]) => {
                    const colorScheme = getColorScheme(access);
                    return (
                      <MenuItem key={lang} sx={{ minWidth: 200 }}>
                        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2">{lang}</Typography>
                          <Chip
                            label={access}
                            size="small"
                            sx={{ 
                              ...(themeVariant === 'vibrant' ? {
                                // Vibrant theme styling for menu chips
                                backgroundColor: darkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.9)',
                                color: getVibrantChipTextColor(access, darkMode),  // Pass darkMode parameter
                                border: '1px solid',
                                borderColor: getVibrantChipBorderColor(access),
                                '& .MuiChip-label': { 
                                  px: 1, 
                                  fontSize: '0.7rem', 
                                  fontWeight: 'bold' 
                                },
                                boxShadow: '0 1px 2px rgba(0,0,0,0.15)'
                              } : {
                                // Standard styling
                                backgroundColor: colorScheme.bg,
                                color: colorScheme.color,
                                border: '1px solid',
                                borderColor: colorScheme.border,
                                '& .MuiChip-label': { px: 1, fontSize: '0.7rem', fontWeight: 'bold' }
                              })
                            }}
                          />
                        </Box>
                      </MenuItem>
                    );
                  })
                ) : (
                  <MenuItem disabled>
                    <Typography variant="body2">No language access defined</Typography>
                  </MenuItem>
                )}
                {userInfo && userInfo.userType === 'admin' && (
                  <>
                    <Divider />
                    <MenuItem sx={{ justifyContent: 'center' }}>
                      <Typography variant="body2" color="error">Admin User (Can Grant Access)</Typography>
                    </MenuItem>
                  </>
                )}
                
              </Menu>
            </Box>
          )}
          
         

          {/* User email with icon - Now with dropdown */}
          <Button
            id="user-profile-button"
            variant="text"
            color="inherit"
            startIcon={<PersonIcon />}
            endIcon={<KeyboardArrowDownIcon fontSize="small" />}
            size="small"
            sx={{ 
              textTransform: 'none',
              color: inVideoEditor ? 'white' : 'inherit',
              fontSize: '0.85rem',
              ...(themeVariant === 'vibrant' && {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '6px',
                padding: '4px 10px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                }
              })
            }}
            onClick={handleUserMenuClick}
            aria-controls={userMenuOpen ? 'user-profile-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={userMenuOpen ? 'true' : undefined}
            className="header-user-menu"
          >
            {userInfo?.email || 'User'}
          </Button>
          
          {/* User Profile Menu */}
          <Menu
            id="user-profile-menu"
            anchorEl={userMenuAnchorEl}
            open={userMenuOpen}
            onClose={handleUserMenuClose}
            MenuListProps={{
              'aria-labelledby': 'user-profile-button',
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem sx={{ pointerEvents: 'none', opacity: 0.7 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                <PersonIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                <Typography variant="body2" flex={1}>
                  {userInfo?.email? userInfo?.email : 'User'}
                </Typography>
                <Chip 
                  label={userInfo?.userType || 'user'} 
                  size="small" 
                  color={userInfo?.userType === 'admin' ? 'error' : 'primary'}
                  variant="outlined"
                  sx={{ ml: 1, fontSize: '0.7rem' }} 
                />
              </Box>
            </MenuItem>
            <Divider />
            
            {/* Admin section - only visible for admin users */}
            {userInfo?.userType === 'admin' && (
              <>
                <Box sx={{ 
                              px: 2, 
                              py: 0.5, 
                              fontSize: '0.75rem', 
                              color: 'text.secondary',
                              fontWeight: 'medium',
                              borderLeft: '3px solid', 
                              borderColor: 'error.main' // Green border
                }}>                  
                    <Typography variant="caption" color="text.secondary">ADMIN</Typography>
                </Box>
                <MenuItem onClick={handleNavigateToAdmin} sx={{ minWidth: '200px', pl: 3 }}>
                  <AdminPanelSettingsIcon fontSize="small" sx={{ mr: 1.5 }} color='error' />
                  Admin Dashboard
                </MenuItem>
                <Divider />
              </>
            )}
            
            {/* System Status section */}
            <Box sx={{ 
                              px: 2, 
                              py: 0.5, 
                              fontSize: '0.75rem', 
                              color: 'text.secondary',
                              fontWeight: 'medium',
                              borderLeft: '3px solid', 
                              borderColor: 'info.main' // Blue border
                }}>                  
                    <Typography variant="caption" color="text.secondary">SYSTEM</Typography>
            </Box>
            <MenuItem onClick={handleNavigateToStatus} sx={{ minWidth: '200px', pl: 3 }}>
              <AssessmentIcon fontSize="small"  color="info" sx={{ mr: 1.5 }} />
              System Status
            </MenuItem>
            <MenuItem onClick={handleNavigateToProcessingStatus} sx={{ minWidth: '200px', pl: 3 }}>
              <VideoFileIcon fontSize="small" color="info" sx={{ mr: 1.5 }} />
              Video Processing Status
            </MenuItem>
            <MenuItem onClick={handleNavigateToActiveSessions}  sx={{ minWidth: '200px', pl: 3 }}>
                <EditIcon fontSize="small" color="info" sx={{ mr: 1.5 }} />
              Active Editing Sessions
            </MenuItem>
            <MenuItem onClick={handleNavigateToEditingStats} sx={{ minWidth: '200px', pl: 3 }}>
              <LeaderboardIcon fontSize="small" color="info" sx={{ mr: 1.5 }} />
              Editing Statistics
            </MenuItem>
            <MenuItem onClick={handleNavigateToTrashBin} sx={{ minWidth: '200px', pl: 3 }}>
              <DeleteIcon fontSize="small" color="info" sx={{ mr: 1.5 }} />
              Trash
            </MenuItem>
            <Divider />
            
            {/* Support section */}
            <Box sx={{ 
                              px: 2, 
                              py: 0.5, 
                              fontSize: '0.75rem', 
                              color: 'text.secondary',
                              fontWeight: 'medium',
                              borderLeft: '3px solid', 
                              borderColor: 'success.main' // Green border
                }}>                  
                    <Typography variant="caption" color="text.secondary">SUPPORT</Typography>
            </Box>
            <MenuItem onClick={handleNavigateToHelp} sx={{ minWidth: '200px', pl: 3 }}>
              <HelpIcon fontSize="small" sx={{ mr: 1.5 }} color="success"/>
              Help Documentation
            </MenuItem>
            <MenuItem onClick={handleNavigateToBugReport} sx={{ minWidth: '200px', pl: 3 }}>
              <BugReportIcon fontSize="small" sx={{ mr: 1.5 }} color="success"/>
              Report a Bug
            </MenuItem>
            <Divider />
            {/* Account section */}
            <Box sx={{ 
                              px: 2, 
                              py: 0.5, 
                              fontSize: '0.75rem', 
                              color: 'text.secondary',
                              fontWeight: 'medium',
                              borderLeft: '3px solid', 
                              borderColor: 'error.main' // Red border
                }}>                  
                    <Typography variant="caption" color="text.secondary">ACCOUNT</Typography>
            </Box>
            <MenuItem onClick={handleChangePassword} sx={{ minWidth: '200px', pl: 3 }}>
              <LockIcon fontSize="small" sx={{ mr: 1.5 }} color="error"/>
              Change Password
            </MenuItem>
          </Menu>

          {showLogout && handleLogout && (
            <Button
              color={themeVariant === 'vibrant' ? 'inherit' : 'primary'}
              variant={themeVariant === 'vibrant' ? 'contained' : 'outlined'}
              size="small"
              startIcon={<LogoutIcon sx={{ color: themeVariant === 'vibrant' ? '#ffffff' : undefined }} />}
              onClick={handleLogout}
              sx={{ 
                ml: 1,
                ...(themeVariant === 'vibrant' && {
                  color: '#ffffff',
                  // Glass-like button style with purple accent
                  backgroundColor: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  fontWeight: '500',
                  borderRadius: '6px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.20)',
                    boxShadow: '0 3px 8px rgba(0,0,0,0.15)',
                    transform: 'translateY(-1px)',
                  }
                })
              }}
            >
              Logout
            </Button>
          )}

          {showDarkMode && (
            <IconButton 
              onClick={toggleDarkMode} 
              color="inherit" 
              className="header-theme-toggle"
              sx={{
                marginLeft: '0.35em',
                ...(themeVariant === 'vibrant' && {
                  color: '#ffffff',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                  }
                })
              }}
            >
              {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          )}
          
        </Toolbar>
      </AppBar>

      {/* Expiration Warning Banner */}
      <ExpirationBanner />

      {/* Change Password Dialog */}
      <Dialog 
        open={changePasswordOpen} 
        onClose={handleClosePasswordDialog} 
        maxWidth="xs" 
        fullWidth
        aria-labelledby="change-password-dialog-title"
        data-testid="change-password-dialog">
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          {passwordSuccess ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              Password changed successfully!
            </Alert>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Please enter your current password and choose a new password.
              </DialogContentText>
              
              {passwordErrors.form && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {passwordErrors.form}
                </Alert>
              )}
              
              <TextField
                margin="dense"
                label="Current Password"
                id="current-password"
                type="password"
                fullWidth
                variant="outlined"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                error={!!passwordErrors.currentPassword}
                helperText={passwordErrors.currentPassword}
                disabled={passwordLoading}
              />
              <TextField
                margin="dense"
                id="new-password"
                label="New Password"
                type="password"
                fullWidth
                variant="outlined"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                error={!!passwordErrors.newPassword}
                helperText={passwordErrors.newPassword}
                disabled={passwordLoading}
              />
              <TextField
                margin="dense"
                id="confirm-password"
                label="Confirm New Password"
                type="password"
                fullWidth
                variant="outlined"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={!!passwordErrors.confirmPassword}
                helperText={passwordErrors.confirmPassword}
                disabled={passwordLoading}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog} disabled={passwordLoading}>
            Cancel
          </Button>
          {!passwordSuccess && (
            <Button 
              onClick={handlePasswordSubmit} 
              disabled={passwordLoading} 
              variant="contained"
              color="primary"
              startIcon={passwordLoading ? <CircularProgress size={20} /> : null}
              data-testid="change-password-submit-button"
            >
              {passwordLoading ? 'Changing...' : 'Change Password'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
